import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faUser,
  faBuilding,
  faPhone,
  faEnvelope,
  faIdCard,
  faBriefcase,
  faMapMarkerAlt,
  faFileAlt,
  faUsers,
  faHome,
  faCity
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import './Contact.css';
import SEO from "../components/SEO";

// 🔹 Offset per lo scroll automatico
const scrollOffset = -225;

function Contact() {
  const location = useLocation();
  const formRef = useRef(null);
  const [formType, setFormType] = useState('azienda');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tipo = params.get('tipo');

    if (tipo === 'privato' || tipo === 'azienda') {
      setFormType(tipo);

      if (formRef.current) {
        const topPos =
          formRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          scrollOffset;

        window.scrollTo({ top: topPos, behavior: 'smooth' });
      }
    }
  }, [location.search]);

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    company: '',
    phone: '',
    email: '',
    vat: '',
    legalForm: '',
    activity: '',
    employees: '',
    legalAddress: '',
    operationalAddress: '',
    fiscalCode: '',
    cap: '',
    description: '',
    contactPreference: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const [sending, setSending] = useState(false);
  const [resultMsg, setResultMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResultMsg('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType, data: formData })
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Invio fallito');

      setResultMsg('Richiesta inviata correttamente. Ti contatteremo a breve.');
      setFormData({
        name: '',
        surname: '',
        company: '',
        phone: '',
        email: '',
        vat: '',
        legalForm: '',
        activity: '',
        employees: '',
        legalAddress: '',
        operationalAddress: '',
        fiscalCode: '',
        cap: '',
        description: '',
        contactPreference: ''
      });
    } catch (err) {
      console.error('Errore invio form:', err);
      setResultMsg("Si è verificato un errore durante l'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const toggleFormType = (type) => {
    setFormType(type);
  };

  // 🎬 Variants per le animazioni
  const sectionVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4, ease: 'easeInOut' } }
  };

  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.2 }
    }
  };

  const fieldVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } }
  };

  return (
    <motion.div variants={sectionVariants} initial="hidden" animate="visible" exit="exit">

      <SEO
        title="Contatti | Andolfatto Insurance Broker"
        description="Richiedi una consulenza o un preventivo assicurativo personalizzato con Andolfatto Insurance Broker. Contattaci e il nostro team ti guiderà nella scelta delle coperture su misura per privati, professionisti e aziende."
        url="https://andolfattobroker.it/contatti"
      />

      <div className="contact-page">
        <section className="contact-hero">
          <h1>Contattaci</h1>
          {/* H2 nascosti SEO */}
          <h2 className="visually-hidden">
            Contatta Andolfatto Insurance Broker a Bassano del Grappa per una consulenza assicurativa personalizzata.
          </h2>

          <h2 className="visually-hidden">
            Richiedi un preventivo assicurativo per privati, professionisti e aziende.
          </h2>

          <h2 className="visually-hidden">
            Compila il modulo di contatto per ricevere assistenza o una consulenza gratuita.
          </h2>

          <h2 className="visually-hidden">
            Broker assicurativo indipendente dal 1974: protezione, trasparenza e supporto costante.
          </h2>

          <h2 className="visually-hidden">
            Scopri come essere ricontattato via telefono o email dal team Andolfatto.
          </h2>
          <p>Se hai bisogno di un preventivo, compila il modulo qui sotto.</p>
        </section>

        <section className="contact-form-section">
          <div className="form-type-selector">
            <button
              className={`form-type-button ${formType === 'azienda' ? 'active' : ''}`}
              onClick={() => toggleFormType('azienda')}
            >
              Azienda
            </button>
            <button
              className={`form-type-button ${formType === 'privato' ? 'active' : ''}`}
              onClick={() => toggleFormType('privato')}
            >
              Privato
            </button>
          </div>

          {/* FORM */}
          <motion.form
            className="contact-form2"
            ref={formRef}
            onSubmit={handleSubmit}
            key={formType}
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            {formType === 'azienda' ? (
              <>
                {/* --- SEZIONE AZIENDA --- */}
                <motion.div className="form-row" variants={fieldVariants}>
                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                      <input
                        type="text"
                        id="company"
                        name="company"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="company">Ragione Sociale*</label>
                    </div>
                  </motion.div>

                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faIdCard} className="input-icon" />
                      <input
                        type="text"
                        id="vat"
                        name="vat"
                        autoComplete="off"
                        value={formData.vat}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="vat">Partita IVA*</label>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div className="form-row" variants={fieldVariants}>
                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faBriefcase} className="input-icon" />
                      <input
                        type="text"
                        id="activity"
                        name="activity"
                        autoComplete="off"
                        value={formData.activity}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="activity">Settore di Attività*</label>
                    </div>
                  </motion.div>

                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faUsers} className="input-icon" />
                      <input
                        type="number"
                        id="employees"
                        name="employees"
                        autoComplete="off"
                        value={formData.employees}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="employees">Numero Dipendenti*</label>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div className="form-row" variants={fieldVariants}>
                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faHome} className="input-icon" />
                      <input
                        type="text"
                        id="legalAddress"
                        name="legalAddress"
                        autoComplete="street-address"
                        value={formData.legalAddress}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="legalAddress">Sede Legale*</label>
                    </div>
                  </motion.div>

                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faCity} className="input-icon" />
                      <input
                        type="text"
                        id="operationalAddress"
                        name="operationalAddress"
                        autoComplete="off"
                        value={formData.operationalAddress}
                        onChange={handleChange}
                        placeholder=" "
                      />
                      <label htmlFor="operationalAddress">Sede Operativa (se diversa)</label>
                    </div>
                  </motion.div>
                </motion.div>
              </>
            ) : (
              <>
                {/* --- SEZIONE PRIVATO --- */}
                <motion.div className="form-row" variants={fieldVariants}>
                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faUser} className="input-icon" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        autoComplete="given-name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="name">Nome*</label>
                    </div>
                  </motion.div>

                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faUser} className="input-icon" />
                      <input
                        type="text"
                        id="surname"
                        name="surname"
                        autoComplete="family-name"
                        value={formData.surname}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="surname">Cognome*</label>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div className="form-row" variants={fieldVariants}>
                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                      <input
                        type="text"
                        id="cap"
                        name="cap"
                        autoComplete="postal-code"
                        value={formData.cap}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="cap">CAP*</label>
                    </div>
                  </motion.div>

                  <motion.div className="form-group" variants={fieldVariants}>
                    <div className="input-wrapper">
                      <FontAwesomeIcon icon={faIdCard} className="input-icon" />
                      <input
                        type="text"
                        id="fiscalCode"
                        name="fiscalCode"
                        autoComplete="off"
                        value={formData.fiscalCode}
                        onChange={handleChange}
                        placeholder=" "
                      />
                      <label htmlFor="fiscalCode">Codice Fiscale</label>
                    </div>
                  </motion.div>
                </motion.div>
              </>
            )}

            {/* --- CAMPI COMUNI --- */}
            <motion.div className="form-row" variants={fieldVariants}>
              <motion.div className="form-group" variants={fieldVariants}>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faPhone} className="input-icon" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="phone">Telefono*</label>
                </div>
              </motion.div>

              <motion.div className="form-group" variants={fieldVariants}>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="email">Email*</label>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="form-group full-width" variants={fieldVariants}>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faFileAlt} className="input-icon" />
                <textarea
                  id="description"
                  name="description"
                  autoComplete="off"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder=" "
                  required
                />
                <label htmlFor="description">Di cosa ha bisogno?*</label>
              </div>
            </motion.div>

            <motion.div className="form-group full-width contact-preference-container" variants={fieldVariants}>
              <div className="contact-preference">
                <select
                  id="contactPreference"
                  name="contactPreference"
                  autoComplete="off"
                  value={formData.contactPreference}
                  onChange={handleChange}
                >
                  <option value="">Seleziona una delle opzioni</option>
                  <option value="Telefono">Telefono</option>
                  <option value="Email">Email</option>
                </select>
                <label htmlFor="contactPreference">Come preferisce essere ricontattato?</label>
              </div>
            </motion.div>

            <motion.div className="button-container" variants={fieldVariants}>
              <button type="submit" className="submit-button" disabled={sending}>
                {sending ? 'Invio…' : 'Invia'}
              </button>
              <button
                type="button"
                className="privacy-button"
                onClick={() =>
                  window.open('https://www.iubenda.com/privacy-policy/86004529', '_blank')
                }
              >
                <FontAwesomeIcon icon={faLock} className="lock-icon" />
                Privacy Policy
              </button>
            </motion.div>

            <AnimatePresence mode="wait">
              {resultMsg && (
                <motion.p
                  className="form-result-message"
                  style={{ marginTop: '10px' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {resultMsg}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.form>
        </section>
      </div>
    </motion.div>
  );
}

export default Contact;
